/**
 * Drill-down tables for the Token Usage tab: by user (with the unattributed bucket kept
 * visibly separate), by action, and the per-session pricing stats.
 *
 * Every table renders its own section shell so one failing endpoint shows an error card
 * in place of that table and leaves the rest of the tab intact.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type {
  UsageByActionTotals,
  UsageByUserTotals,
  UsageSessionTotals,
  UsageSessionPricingStats,
} from '@/services/adminApi';
import {
  UNATTRIBUTED_REASON_LABELS,
  classifyUsageActor,
  formatActionName,
  formatOpaqueId,
  partitionUsers,
} from './tokenUsageAttribution';
import {
  describeSessionCoverage,
  formatExactTokens,
  formatTokensPerHour,
} from './tokenUsageView';

export const UNATTRIBUTED_BUCKET_LABEL = 'Unattributed (service / pre-migration)';

/** Inline, scoped failure affordance — mirrors OrgDetailPage's SectionError idiom. */
export function TokenUsageSectionError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
    >
      {message}
    </div>
  );
}

interface SectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

export function TokenUsageSection({ title, description, children }: SectionProps) {
  return (
    <section className="mb-6 border border-border rounded-md p-4 bg-card">
      <div className="mb-3">
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function OpaqueId({ id }: { id: string }) {
  // No directory lookup exists here, so every actor renders as its raw id. The full
  // value stays available on hover rather than being lost to truncation.
  return (
    <code className="text-xs" title={id}>
      {formatOpaqueId(id)}
    </code>
  );
}

function SplitCells({
  successTokens,
  failedTokens,
}: {
  successTokens: number;
  failedTokens: number;
}) {
  return (
    <>
      <TableCell className="text-right">{formatExactTokens(successTokens)}</TableCell>
      <TableCell className="text-right">
        {failedTokens > 0 ? (
          <span className="text-destructive">
            {formatExactTokens(failedTokens)}
          </span>
        ) : (
          formatExactTokens(failedTokens)
        )}
      </TableCell>
    </>
  );
}

export interface ByUserTableProps {
  users: UsageByUserTotals[];
}

export function ByUserTable({ users }: ByUserTableProps) {
  const { users: people, unattributed, userTokens, unattributedTokens } =
    partitionUsers(users);

  if (people.length === 0 && unattributed.length === 0) {
    return <EmptyRowsNote label="No per-user spend recorded in this window." />;
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead className="text-right">Total tokens</TableHead>
            <TableHead className="text-right">Succeeded</TableHead>
            <TableHead className="text-right">Failed</TableHead>
            <TableHead className="text-right">Calls</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {people.map(row => (
            <TableRow key={row.userId}>
              <TableCell>
                <OpaqueId id={row.userId} />
              </TableCell>
              <TableCell className="text-right">
                {formatExactTokens(row.totalTokens)}
              </TableCell>
              <SplitCells
                successTokens={row.successTokens}
                failedTokens={row.failedTokens}
              />
              <TableCell className="text-right">
                {formatExactTokens(row.successCount + row.failedCount)}
              </TableCell>
            </TableRow>
          ))}
          {people.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="text-sm text-muted-foreground">
                No spend attributed to an identified user in this window.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <p className="mt-2 text-xs text-muted-foreground">
        Attributed to identified users: {formatExactTokens(userTokens)} tokens.
      </p>

      {unattributed.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-foreground">
            {UNATTRIBUTED_BUCKET_LABEL}
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5 mb-2">
            Spend recorded without an end user — pre-migration lump sums and calls made
            under a service credential. Kept separate from the user table above, never
            merged into it.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recorded id</TableHead>
                <TableHead>Why unattributed</TableHead>
                <TableHead className="text-right">Total tokens</TableHead>
                <TableHead className="text-right">Succeeded</TableHead>
                <TableHead className="text-right">Failed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unattributed.map(row => {
                const classification = classifyUsageActor(row.userId);
                const reason =
                  classification.kind === 'unattributed'
                    ? UNATTRIBUTED_REASON_LABELS[classification.reason]
                    : '';
                return (
                  <TableRow key={row.userId}>
                    <TableCell>
                      <OpaqueId id={row.userId} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {reason}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatExactTokens(row.totalTokens)}
                    </TableCell>
                    <SplitCells
                      successTokens={row.successTokens}
                      failedTokens={row.failedTokens}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <p className="mt-2 text-xs text-muted-foreground">
            Unattributed: {formatExactTokens(unattributedTokens)} tokens.
          </p>
        </div>
      )}
    </>
  );
}

export interface ByActionTableProps {
  actions: UsageByActionTotals[];
}

export function ByActionTable({ actions }: ByActionTableProps) {
  if (actions.length === 0) {
    return <EmptyRowsNote label="No per-action spend recorded in this window." />;
  }

  const sorted = [...actions].sort((a, b) => b.totalTokens - a.totalTokens);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Action</TableHead>
          <TableHead className="text-right">Total tokens</TableHead>
          <TableHead className="text-right">Succeeded</TableHead>
          <TableHead className="text-right">Failed</TableHead>
          <TableHead className="text-right">Calls</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map(row => (
          <TableRow key={row.actionName ?? '__no_action__'}>
            <TableCell>
              {row.actionName === null ? (
                <span className="text-muted-foreground italic">
                  {formatActionName(null)}
                </span>
              ) : (
                row.actionName
              )}
            </TableCell>
            <TableCell className="text-right">
              {formatExactTokens(row.totalTokens)}
            </TableCell>
            <SplitCells
              successTokens={row.successTokens}
              failedTokens={row.failedTokens}
            />
            <TableCell className="text-right">
              {formatExactTokens(row.successCount + row.failedCount)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export interface SessionPricingPanelProps {
  sessions: UsageSessionTotals[];
  stats: UsageSessionPricingStats;
  /** Longest per-session list rendered; the stats above always cover every session. */
  maxRows?: number;
}

export function SessionPricingPanel({
  sessions,
  stats,
  maxRows = 25,
}: SessionPricingPanelProps) {
  if (stats.sessionCount === 0) {
    return <EmptyRowsNote label="No sessions recorded in this window." />;
  }

  const coverage = describeSessionCoverage(stats);
  const perHour = formatTokensPerHour(stats.averageTokensPerHour);
  const shown = [...sessions]
    .sort((a, b) => b.totalTokens - a.totalTokens)
    .slice(0, maxRows);

  return (
    <>
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Stat label="Sessions" value={stats.sessionCount.toLocaleString('en-US')} />
        <Stat
          label="Avg tokens / session"
          value={Math.round(stats.averageTokensPerSession).toLocaleString('en-US')}
        />
        <Stat
          label="Avg tokens / hour"
          value={perHour ?? 'No duration recorded'}
        />
        <Stat
          label="Sessions with duration"
          value={`${stats.sessionsWithDurationCount.toLocaleString('en-US')} of ${stats.sessionCount.toLocaleString('en-US')}`}
        />
      </dl>

      {coverage.text && (
        <p className="mb-3 text-xs text-warning">{coverage.text}</p>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Session</TableHead>
            <TableHead className="text-right">Total tokens</TableHead>
            <TableHead className="text-right">Calls</TableHead>
            <TableHead className="text-right">Duration</TableHead>
            <TableHead className="text-right">Tokens / hour</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map(row => {
            const rate = formatTokensPerHour(row.tokensPerHour);
            return (
              <TableRow key={row.sessionId}>
                <TableCell>
                  <OpaqueId id={row.sessionId} />
                </TableCell>
                <TableCell className="text-right">
                  {formatExactTokens(row.totalTokens)}
                </TableCell>
                <TableCell className="text-right">
                  {formatExactTokens(row.requestCount)}
                </TableCell>
                <TableCell className="text-right">
                  {row.durationSeconds === null
                    ? '—'
                    : `${Math.round(row.durationSeconds / 60).toLocaleString('en-US')} min`}
                </TableCell>
                <TableCell className="text-right">{rate ?? '—'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {sessions.length > shown.length && (
        <p className="mt-2 text-xs text-muted-foreground">
          Showing the top {shown.length} of {sessions.length} sessions by spend. The
          statistics above cover all {stats.sessionCount} sessions in range.
        </p>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold text-foreground">{value}</dd>
    </div>
  );
}

function EmptyRowsNote({ label }: { label: string }) {
  return <p className="text-sm text-muted-foreground py-4">{label}</p>;
}
